import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ClearSharp, Delete, Edit, ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import dayjs from "dayjs";
import theme from "../../style/theme";
import { axiosRequest } from "../../service/AxiosService";
import CustomMenu from "../../components/CustomMenu/CustomMenu";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import NewsEditDialog from "./components/NewsEditDialog";

type NewsStatusKey = "all" | "draft" | "scheduled" | "published" | "deleted";

export type NewsItem = {
  id: string;
  category: string;
  title: string;
  contents?: string;
  publishedAt: string | null;
  isDeleted: boolean;
  status: "下書き" | "予約" | "公開" | "削除";

  createdAt?: string;
  updatedAt?: string;
  etag?: string;
};

const defaultPageSize = 50;
const pageSizeList = [50, 100, 1000];

const storageKeys = {
  keyword: "keyword_News",
  category: "category_News",
  dateFrom: "publishedFrom_News",
  dateTo: "publishedTo_News",
  status: "status_News",
  pageSize: "showItems_News",
  page: "paging_News",
  visibleColumns: "visibleColumns_News",
};

const columnLabels: Record<string, string> = {
  publishedAt: "公開日",
  category: "カテゴリ",
  title: "タイトル",
  status: "ステータス",
  actions: "操作",
  createdAt: "作成日時",
  updatedAt: "更新日時",
};

const statusLabels: { key: NewsStatusKey; label: string }[] = [
  { key: "all", label: "すべて" },
  { key: "draft", label: "下書き" },
  { key: "scheduled", label: "予約" },
  { key: "published", label: "公開" },
  { key: "deleted", label: "削除" },
];

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY/MM/DD HH:mm") : "-";
};

const NewsManagement: React.FC = () => {
  const navigate = useNavigate();

  // 一覧データ
  const [rows, setRows] = useState<NewsItem[]>([]);
  const [total, setTotal] = useState(0);

  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = sessionStorage.getItem(storageKeys.pageSize);
    return stored ? Number(stored) : defaultPageSize;
  });
  const [page, setPage] = useState<number>(() => {
    const stored = sessionStorage.getItem(storageKeys.page);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem(storageKeys.visibleColumns);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return new Set(parsed);
      } catch (_) { }
    }
    return new Set(Object.keys(columnLabels));
  });

  // フィルタ
  const [keyword, setKeyword] = useState<string>(() => sessionStorage.getItem(storageKeys.keyword) ?? "");
  const [category, setCategory] = useState<string>(() => sessionStorage.getItem(storageKeys.category) ?? "");
  const [status, setStatus] = useState<NewsStatusKey>(() => {
    const stored = sessionStorage.getItem(storageKeys.status) as NewsStatusKey | null;
    return stored ?? "all";
  });
  const [publishedFrom, setPublishedFrom] = useState<string>(() => sessionStorage.getItem(storageKeys.dateFrom) ?? "");
  const [publishedTo, setPublishedTo] = useState<string>(() => sessionStorage.getItem(storageKeys.dateTo) ?? "");

  // カテゴリ選択肢
  const categoryOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.category).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  // UI状態
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; target?: NewsItem }>({ open: false });
  const [showSnackbar, setShowSnackbar] = useState(false);

  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem(storageKeys.visibleColumns, JSON.stringify(Array.from(newSet)));
    setVisibleColumns(newSet);
  };

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosRequest<NewsItem[]>("/admin/news", "GET");
      setRows(res ?? []);
      setTotal(res?.length ?? 0);
    } catch (e) {
      console.error(e);
      setSnackbarMessage("ニュース一覧の取得に失敗しました。");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleStatusChipClick = (next: NewsStatusKey) => {
    setStatus(next);
    sessionStorage.setItem(storageKeys.status, next);
    // フィルタ変更時は1ページ目へ
    setPage(1);
    sessionStorage.removeItem(storageKeys.page);
  };

  const filterReset = () => {
    sessionStorage.removeItem(storageKeys.keyword);
    sessionStorage.removeItem(storageKeys.category);
    sessionStorage.removeItem(storageKeys.dateFrom);
    sessionStorage.removeItem(storageKeys.dateTo);
    sessionStorage.removeItem(storageKeys.status);
    sessionStorage.removeItem(storageKeys.page);
    sessionStorage.removeItem(storageKeys.pageSize);
    sessionStorage.removeItem(storageKeys.visibleColumns);

    setKeyword("");
    setCategory("");
    setPublishedFrom("");
    setPublishedTo("");
    setStatus("all");
    setPage(1);
    setPageSize(defaultPageSize);
    setVisibleColumns(new Set(Object.keys(columnLabels)));
  };

  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleDelete = async (target: NewsItem) => {
    try {
      await axiosRequest(`/admin/news/${target.id}`, "DELETE");
      setSnackbarMessage("ニュースを削除しました。");
      setShowSnackbar(true);
      setDeleteDialog({ open: false });
      fetchNews();
    } catch (e) {
      console.error(e);
      setSnackbarMessage("ニュースの削除に失敗しました。");
      setShowSnackbar(true);
    }
  };

  const handleEditClick = async (row: NewsItem) => {
    try {
      const res = await axiosRequest<{
        detail: NewsItem;
        etag: string;
      }>(`/admin/news/${row.id}`, "GET");

      // レスポンスボディに etag が含まれている場合
      const freshData: NewsItem = {
        ...res.detail,
        etag: res.etag,
      };
      setEditDialog({ open: true, target: freshData });
    } catch (e) {
      console.error("詳細取得に失敗しました", e);
      // フォールバック：一覧データで開く（ETag なし → 保存時に 428）
      setEditDialog({ open: true, target: row });
    }
  };

  const handleSaveNews = async (data: Partial<NewsItem>) => {
    try {
      if (editDialog.target) {
        // 更新
        await axiosRequest(`/admin/news/${editDialog.target.id}`, "PUT", {
          data,
          headers: {
            "If-Match": editDialog.target.etag ?? "",
          },
        });
        setSnackbarMessage("ニュースを更新しました。");
      } else {
        // 新規
        await axiosRequest("/admin/news", "POST", { data });
        setSnackbarMessage("ニュースを作成しました。");
      }
      fetchNews();
      setShowSnackbar(true);
    } catch (e: any) {
      if (e?.response?.status === 412) {
        setSnackbarMessage("他のユーザーによって更新されています。最新のデータを取得してから再度更新してください。");
        setShowSnackbar(true);
        fetchNews(); // 一覧を再取得
      } else if (e?.response?.status === 428) {
        setSnackbarMessage("更新に必要な情報が不足しています。再度編集画面を開いてください。");
        setShowSnackbar(true);
      } else {
        console.error(e);
        setSnackbarMessage("ニュースの保存に失敗しました。");
        setShowSnackbar(true);
      }
    }
  };

  // テーブルのスタイル設定
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.custom.gray,
      color: theme.palette.common.white,
      whiteSpace: "nowrap",
      padding: "6px 20px",
    },
    [`&.${tableCellClasses.body}`]: {
      whiteSpace: "normal",
      padding: "6px 20px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
    "&:hover": {
      backgroundColor: "#e3f2fd",
      boxShadow: "inset 0 0 0 1px #90caf9",
    },
  }));

  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    target?: NewsItem;
  }>({ open: false });

  //フィルタ機能
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      // ステータス
      if (status !== "all") {
        if (
          (status === "draft" && r.status !== "下書き") ||
          (status === "scheduled" && r.status !== "予約") ||
          (status === "published" && r.status !== "公開") ||
          (status === "deleted" && r.status !== "削除")
        ) return false;
      }

      // キーワード
      if (keyword) {
        const kw = keyword.toLowerCase();
        const target =
          (r.title ?? "").toLowerCase() +
          (r.contents ?? "").toLowerCase();

        if (!target.includes(kw)) return false;
      }

      // カテゴリ
      if (category && r.category !== category) return false;

      // 公開日 From
      if (publishedFrom && r.publishedAt) {
        if (dayjs(r.publishedAt).isBefore(dayjs(publishedFrom))) return false;
      }

      // 公開日 To
      if (publishedTo && r.publishedAt) {
        if (
          dayjs(r.publishedAt).isAfter(dayjs(publishedTo).endOf("day"))
        ) return false;
      }

      return true;
    });
  }, [rows, status, keyword, category, publishedFrom, publishedTo]);

  // ソート対象キー型
  type SortKey = keyof Pick<
    NewsItem,
    "publishedAt" | "category" | "title" | "status" | "createdAt" | "updatedAt"
  >;

  // ソート設定
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; asc: boolean }>({
    key: "publishedAt",
    asc: false,
  });

  // ヘッダーのホバー
  const [hoveredColumn, setHoveredColumn] = useState<SortKey | null>(null);

  const sortedFilteredRows = useMemo(() => {
    const result = [...filteredRows];
    const { key, asc } = sortConfig;

    const getComparable = (row: NewsItem): string | number | null => {
      const v = row[key];
      if (key === "publishedAt" || key === "createdAt" || key === "updatedAt") {
        if (!v) return null;
        const d = dayjs(v);
        return d.isValid() ? d.valueOf() : null;
      }
      return (v ?? "") as string;
    };

    result.sort((a, b) => {
      const av = getComparable(a);
      const bv = getComparable(b);

      // 下書きの並び順を後ろにする
      if (key === "publishedAt") {
        const aNull = av == null;
        const bNull = bv == null;

        // 両方null
        if (aNull && bNull) return 0;

        // nullは常に最後（昇順・降順関係なし）
        if (aNull) return 1;
        if (bNull) return -1;
      }


      // number（日付）比較
      if (typeof av === "number" && typeof bv === "number") {
        return asc ? av - bv : bv - av;
      }

      // string 比較
      const as = String(av);
      const bs = String(bv);
      return asc ? as.localeCompare(bs) : bs.localeCompare(as);
    });

    return result;
  }, [filteredRows, sortConfig]);

  //ステータス表記
  const getStatusChipStyle = (status: string) => {
    switch (status) {
      case "公開":
        return {
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
        };
      case "予約":
        return {
          backgroundColor: "#fff",
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
        };
      case "下書き":
        return {
          backgroundColor: "#fff",
          color: theme.palette.primary.dark,
          border: `1px solid ${theme.palette.primary.dark}`,
        };
      case "削除":
        return {
          backgroundColor: "#757575",
          color: "#fff",
        };
      default:
        return {};
    }
  };

  //ページング
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedFilteredRows.slice(start, end);
  }, [sortedFilteredRows, page, pageSize]);


  const renderSortableHeader = (key: SortKey, label: string) => {
    const isActive = sortConfig.key === key;
    const asc = sortConfig.asc;

    return (
      <StyledTableCell
        onClick={() => {
          setPage(1);
          sessionStorage.removeItem(storageKeys.page);
          setSortConfig(
            isActive
              ? { key, asc: !asc }
              : { key, asc: true }
          );
        }}
        onMouseEnter={() => setHoveredColumn(key)}
        onMouseLeave={() => setHoveredColumn(null)}
        sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {label}
          {(isActive || hoveredColumn === key) && (
            isActive ? (asc ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)
              : <ExpandMore fontSize="small" style={{ opacity: 0.35 }} />
          )}
        </span>
      </StyledTableCell>
    );
  };

  const dataColumnKeys: SortKey[] = ["publishedAt", "category", "title", "status", "createdAt", "updatedAt"];
  const visibleDataCols = dataColumnKeys.filter(k => visibleColumns.has(k)).length;
  const colSpanCount = visibleDataCols + 1;


  const PagingComponent = ({
    total,
    page,
    setPage,
    pageSize,
    position,
  }: {
    position: "top" | "bottom";
    total: number;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    pageSize: number;
  }) => {
    const totalPages = Math.ceil(total / pageSize);

    return (
      <Box
        sx={{
          ml: 2,
          mt: position === "top" ? 1 : "auto",
          mb: 1,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <div style={{ marginTop: 1, color: "#666", fontSize: "1rem" }}>
          全 {total} 件中{" "}
          {total === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * pageSize + 1} 件 〜 ${Math.min(
              page * pageSize,
              total
            )} 件`}
          を表示
        </div>

        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          size="small"
        />
      </Box>
    );
  };


  return (
    <CustomMenu>
      <Box sx={{ p: 2 }}>
        {/* ヘッダ */}

        {/* フィルタ */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          {/* キーワード */}
          <TextField
            label="キーワード"
            size="small"
            value={keyword}
            onChange={(e) => {
              const v = e.target.value;
              setKeyword(v);
              v === "" ? sessionStorage.removeItem(storageKeys.keyword) : sessionStorage.setItem(storageKeys.keyword, v);
            }}
            sx={{ width: 240 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: keyword ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setKeyword("");
                        sessionStorage.removeItem(storageKeys.keyword);
                      }}
                      edge="end"
                      sx={{ backgroundColor: "transparent", "&:hover": { backgroundColor: "transparent" } }}
                    >
                      <ClearSharp fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {/* カテゴリ */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={category}
              onChange={(e) => {
                const v = e.target.value as string;
                setCategory(v);
                v === "" ? sessionStorage.removeItem(storageKeys.category) : sessionStorage.setItem(storageKeys.category, v);
                setPage(1);
                sessionStorage.removeItem(storageKeys.page);
              }}
            >
              <MenuItem value="">
                <em>カテゴリ（すべて）</em>
              </MenuItem>
              {categoryOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 公開日 From/To */}
          <TextField
            label="公開日 From"
            type="date"
            size="small"
            value={publishedFrom}
            onChange={(e) => {
              const v = e.target.value;
              setPublishedFrom(v);
              v === "" ? sessionStorage.removeItem(storageKeys.dateFrom) : sessionStorage.setItem(storageKeys.dateFrom, v);
              setPage(1);
              sessionStorage.removeItem(storageKeys.page);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="公開日 To"
            type="date"
            size="small"
            value={publishedTo}
            onChange={(e) => {
              const v = e.target.value;
              setPublishedTo(v);
              v === "" ? sessionStorage.removeItem(storageKeys.dateTo) : sessionStorage.setItem(storageKeys.dateTo, v);
              setPage(1);
              sessionStorage.removeItem(storageKeys.page);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />

          <Button variant="text" onClick={filterReset}>
            クリア
          </Button>
        </Box>

        {/* ステータス（Chip単一選択） */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mr: 1 }}>
            ステータス：
          </Typography>
          {statusLabels.map((s) => (
            <Chip
              key={s.key}
              label={s.label}
              clickable
              onClick={() => handleStatusChipClick(s.key)}
              sx={{
                backgroundColor: status === s.key ? theme.palette.custom?.blue ?? theme.palette.primary.main : "grey.200",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: status === s.key ? "primary.dark" : "grey.400",
                },
              }}
            />
          ))}

          <Chip
            label="リセット"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
              ml: 1,
            }}
            onClick={filterReset}
          />
        </Box>

        {/* 表示設定 */}
        <Button
          sx={{ mb: 1 }}
          onClick={() => setShowColumnSettings((v) => !v)}
          startIcon={showColumnSettings ? <ExpandLess /> : <ExpandMore />}
        >
          表示設定
        </Button>
        {showColumnSettings && (
          <Box sx={{ pl: 2, pb: 2 }}>
            {/* 表示件数 */}
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: "bold", color: "grey" }}>表示件数：</Typography>
              <RadioGroup
                row
                value={String(pageSize)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setPageSize(v);
                  sessionStorage.setItem(storageKeys.pageSize, e.target.value);
                  setPage(1);
                  sessionStorage.removeItem(storageKeys.page);
                }}
              >
                {pageSizeList.map((n) => (
                  <FormControlLabel
                    key={n}
                    value={String(n)}
                    control={<Radio sx={{ transform: "scale(0.6)" }} />}
                    label={`${n}件`}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* 表示項目 */}
            <Box>
              <Typography sx={{ fontWeight: "bold", color: "grey", mb: 1 }}>表示項目：</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {Object.entries(columnLabels)
                  .filter(([k]) => k !== "actions")
                  .map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(key)}
                        onChange={() => toggleColumn(key)}
                      />
                      {label}
                    </label>
                  ))}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setEditDialog({ open: true })}
          >
            ニュース新規作成
          </Button>
        </Box>

        {/* ページング表示 */}
        <PagingComponent
          position="top"
          total={filteredRows.length}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
        />

        <TableContainer
          component={Paper}
          sx={{
            mx: 1,
            mb: 2,
            overflow: "auto",
            maxHeight: "calc(100vh - 360px)",
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 800 }}>

            <TableHead>
              <TableRow>

                {visibleColumns.has("publishedAt") && renderSortableHeader("publishedAt", columnLabels.publishedAt)}
                {visibleColumns.has("category") && renderSortableHeader("category", columnLabels.category)}
                {visibleColumns.has("title") && renderSortableHeader("title", columnLabels.title)}
                {visibleColumns.has("status") && renderSortableHeader("status", columnLabels.status)}
                {visibleColumns.has("createdAt") && renderSortableHeader("createdAt", columnLabels.createdAt)}
                {visibleColumns.has("updatedAt") && renderSortableHeader("updatedAt", columnLabels.updatedAt)}
                <StyledTableCell>{columnLabels.actions}</StyledTableCell>

              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((r) => {

                const statusLabel = r.status;

                return (
                  <StyledTableRow key={r.id}>
                    {visibleColumns.has("publishedAt") && <StyledTableCell>{formatDateTime(r.publishedAt)}</StyledTableCell>}
                    {visibleColumns.has("category") && <StyledTableCell>{r.category}</StyledTableCell>}
                    {visibleColumns.has("title") && <StyledTableCell>{r.title}</StyledTableCell>}
                    {visibleColumns.has("status") && (
                      <StyledTableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            height: "25px",
                            "& .MuiChip-label": {
                              paddingLeft: "10px",
                              paddingRight: "10px",
                            },
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            ...getStatusChipStyle(r.status),
                          }}
                        />
                      </StyledTableCell>
                    )}

                    {visibleColumns.has("createdAt") && <StyledTableCell>{formatDateTime(r.createdAt)}</StyledTableCell>}
                    {visibleColumns.has("updatedAt") && <StyledTableCell>{formatDateTime(r.updatedAt)}</StyledTableCell>}

                    <StyledTableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => handleEditClick(r)}
                        >
                          編集
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Delete />}
                          onClick={() => setDeleteDialog({ open: true, target: r })}
                        >
                          削除
                        </Button>
                      </Box>

                    </StyledTableCell>
                  </StyledTableRow>

                );
              })}

              {filteredRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={colSpanCount}>
                    <Typography sx={{ py: 2, textAlign: "center", color: "text.secondary" }}>
                      該当するニュースがありません。
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {loading && (
                <TableRow>
                  <TableCell colSpan={colSpanCount}>
                    <Typography sx={{ py: 2, textAlign: "center", color: "text.secondary" }}>
                      読み込み中...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ページング */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
          <PagingComponent
            position="bottom"
            total={filteredRows.length}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
          />
        </Box>

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
          <DialogTitle>確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              選択したニュースを削除しますか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false })}>キャンセル</Button>
            <Button
              color="error"
              onClick={() => {
                if (deleteDialog.target) handleDelete(deleteDialog.target);
              }}
            >
              削除
            </Button>
          </DialogActions>
        </Dialog>

        <NewsEditDialog
          open={editDialog.open}
          initialData={editDialog.target}
          onClose={() => setEditDialog({ open: false })}
          onSave={handleSaveNews}
        />

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={showSnackbar}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMessage}
          autoHideDuration={6000}
        />
      </Box>
    </CustomMenu>
  );
};

export default NewsManagement;